import { ImageModel } from "../models/image";
import placeholderImage from "../assets/image_placeholder.png";
import orgPlaceholderImage from "../assets/image_placeholder_org.png";
const ImageConstants = {
    maxWidth: 512,
    maxHeight: 512
};
const ccImageId = "ccimg://";
// Due to Webpack's url-loader, historically the placeholder image has been
// converted to a data URI at bundle time. Therefore, the placeholder "url"
// was a data URI that contained the placeholder image data. When the
// placeholder image was changed, this was changed so that now the placeholder
// image is represented by a URL, but the original data URI placeholder image
// is still present in documents.
export function isPlaceholderImage(url) {
    return url && (url === placeholderImage) || (url === orgPlaceholderImage);
}
function kUploadImage(db, image) {
    const img = {
        imageUrl: placeholderImage,
        imageData: placeholderImage
    };
    return new Promise((resolve, reject) => db.addImage(image).then(dbImage => {
        img.imageKey = dbImage.image.self.imageKey;
        img.imageData = dbImage.image.imageData;
        // starting with 2.1.3, image url includes class hash and image key
        img.imageUrl = getCCImagePath(`${db.stores.user.classHash}/${dbImage.image.self.imageKey}`);
        return fetch(img.imageData);
    })
        .then(response => response.blob())
        .then(blob => {
        img.imageData = URL.createObjectURL(blob);
        resolve(img);
    })
        .catch(() => {
        reject();
    }));
}
export function getCCImagePath(imageKey) {
    return ccImageId + imageKey;
}
export function storeCorsImage(db, imagePath) {
    return storeImage(db, imagePath, imagePath, true);
}
export function storeFileImage(db, file) {
    const url = URL.createObjectURL(file);
    return storeImage(db, url, file.name);
}
export function storeImage(db, url, name, cors) {
    const img = {
        imageUrl: placeholderImage,
        imageData: placeholderImage
    };
    return new Promise((resolve, reject) => {
        if (!url)
            reject(img);
        // This downloads the url onto a canvas and returns a data url for the canvas.
        resizeImage(url, ImageConstants.maxWidth, ImageConstants.maxHeight, cors)
            .then(imageData => {
            const _name = name || url;
            const image = ImageModel.create({
                key: "",
                imageData,
                title: _name,
                originalSource: _name,
                createdAt: 0,
                createdBy: db.stores.user.id
            });
            // This does not download the imageData again, but it does use
            // fetch to turn it into a blob object.
            kUploadImage(db, image).then(storedImage => {
                resolve(storedImage);
            }).catch(() => {
                resolve(img);
            });
        })
            .catch((e) => {
            if ((e instanceof Error) && (e.message.indexOf("converting") >= 0)) {
                resolve(img);
            }
            else {
                reject(e);
            }
        });
    });
}
// Image size functions
// Photographic content is an order of magnitude smaller as JPEG than as lossless PNG, and
// these images are stored base64 in the realtime database, where that size is paid per class.
const kJpegQuality = 0.85;
/**
 * Encodes a canvas as a data url, preferring JPEG.
 *
 * JPEG has no alpha channel, so anything transparent would composite to black. Only fully
 * opaque images are encoded that way; everything else keeps PNG. A canvas tainted by a
 * cross-origin source cannot be inspected at all, so it also keeps PNG — and `toDataURL`
 * will throw on it either way, which the caller already handles.
 */
export function toCompactDataUrl(canvas) {
    return hasTransparency(canvas) ? canvas.toDataURL() : canvas.toDataURL("image/jpeg", kJpegQuality);
}
function hasTransparency(canvas) {
    try {
        const context = canvas.getContext("2d");
        if (!context)
            return true;
        const { data } = context.getImageData(0, 0, canvas.width, canvas.height);
        for (let i = 3; i < data.length; i += 4) {
            if (data[i] < 255)
                return true;
        }
        return false;
    }
    catch (_a) {
        return true;
    }
}
function resizeImage(imageUrl, maxWidth, maxHeight, cors) {
    return new Promise((resolve, reject) => {
        const image = new Image();
        if (cors) {
            // This is necessary for access to firebase storage urls, (and presumably other
            // cors-aware resources), but prevents generic urls from loading successfully.
            image.crossOrigin = "anonymous";
        }
        image.onload = () => {
            const width = image.width;
            const height = image.height;
            let newWidth;
            let newHeight;
            if (width <= maxWidth && height <= maxHeight) {
                newWidth = width;
                newHeight = height;
            }
            else if (width > height) {
                newHeight = height * (maxWidth / width);
                newWidth = maxWidth;
            }
            else {
                newWidth = width * (maxHeight / height);
                newHeight = maxHeight;
            }
            const canvas = document.createElement("canvas");
            canvas.width = newWidth;
            canvas.height = newHeight;
            const context = canvas.getContext("2d");
            context.drawImage(image, 0, 0, newWidth, newHeight);
            // Return Base64 string of image
            try {
                resolve(toCompactDataUrl(canvas));
            }
            catch (e) {
                reject(new Error(`Error converting image: ${imageUrl}`));
            }
        };
        image.onerror = (e) => {
            reject(new Error(`Error loading image: ${imageUrl}`));
        };
        image.src = imageUrl;
    });
}
export function getImageDimensions(url) {
    return new Promise((resolve, reject) => {
        const image = new Image();
        image.onload = () => {
            const dimensions = { src: image.src, width: image.width, height: image.height };
            resolve(dimensions);
        };
        image.onerror = (e) => {
            reject(new Error(`Error getting dimensions of image: ${url}`));
        };
        image.src = url;
    });
}
export function getFileImageDimensions(file) {
    return getImageDimensions(URL.createObjectURL(file));
}
//# sourceMappingURL=data:application/json;base64,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